
import { Component, Vue, Prop, Watch, Emit } from "vue-property-decorator";
import { namespace } from "vuex-class";
import Tree from "../Tree.vue";
import { SiteTreeNode, Group } from "@/store/eventqueue/types";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { clearTimeout } from "timers";
import AreaFinderStatus from "@/components/AreaFinderStatus.vue";

const Eventqueue = namespace("eventqueue");

/**
 * @deprecated This component is deprecated as it has a negative performance impact on large customers. Use the AreaTreeSelect component instead.
 */
@Component({
	components: {
		tree: Tree,
		VuePerfectScrollbar: VuePerfectScrollbar,
		"area-finder-status": AreaFinderStatus
	}
})
export default class AreaSelect extends Vue {
	$refs!: {
		areaTreeScrollBar: any;
		toggleBox: HTMLDivElement;
		searchBox: HTMLInputElement;
	};

	@Eventqueue.Getter("getSiteTree") siteTree: SiteTreeNode[];
	@Eventqueue.Getter getSiteTreeForSelectedTenant: (selectedTenantGroupId: number) => SiteTreeNode[];

	@Eventqueue.Action collapseSiteTree: ({ groups, lvl, collapsedLevel }) => void;
	@Eventqueue.Action expandSiteTree: () => void;
	@Eventqueue.Action findGroupInTreeById: (groupId: number) => SiteTreeNode;
	@Eventqueue.Action sitesTreeGet: (multiTenant: boolean) => Promise<void>;

	@Eventqueue.Mutation setSiteTreeFilter: (search: string) => void;
	@Eventqueue.Mutation setSiteTreeFilterOff: (treeNode: SiteTreeNode) => void;
	@Eventqueue.Mutation setSiteTreeFilterOn: ({ treeNode, shown }) => void;

	@Prop(Boolean) collapsed: boolean;
	@Prop(Number) collapsedLevel: number;

	@Prop({ type: Boolean, default: false }) multiple: boolean;
	@Prop({ type: Boolean, default: false }) multiTenant: boolean;
	@Prop({ type: Boolean, default: false }) readonly: boolean;

	@Prop({ type: Number, default: -1 }) selectedTenantGroupId: number;
	@Prop({ type: String, default: "" }) enabledFlag : string;

	@Prop({ type: Boolean, default: false }) selectDisabled: boolean;

	@Prop()
	public value: any;

	@Prop({ type: String })
	public actionOnSelect: string;

	public areaSearch: string = "";
	public searchResults: any[] = [];
	public clicks: number = 0;
	public clickTimer: number | null = null;

	private lastFilter: string = "";
	private filterTimeout: any = null;

	private filterFocus: boolean = false;

	private selectedGroups: any[] = [];

	public sitesLoading: boolean = false;

	public async beforeMount() {
		// Listens to sibling components for when they
		// emit an event to this component to update the selected area.
		this.$root.$on('updateGroupID', item => {
			this.$emit("input", item);
		})
		
		try {
			this.sitesLoading = true;
			await this.sitesTreeGet(this.multiTenant);
			this.sitesLoading = false;
		}
		catch {
			this.sitesLoading = false;
		}

		this.$emit("component-loaded");
	}

	public async mounted() {
		if (this.value) {
			this.updateFromValue();
		}
	}

	public beforeDestroy() {
		// Unhook listener before component is destroyed.
		this.$root.$off('updateGroupID', item => {
			this.$emit("input", item);
		})
	}

	public get tree(): SiteTreeNode[] {
		if (this.multiTenant && this.selectedTenantGroupId !== -1) {
			return this.getSiteTreeForSelectedTenant(this.selectedTenantGroupId);
		} else {
			return this.siteTree;
		}
	}

	@Watch("value")
	private valueChanged(value: string) {
		this.updateFromValue();
	}

	/**
	 * Update selected groups list based on model change
	 */
	private async updateFromValue() {
		if (!this.selectedGroups) {
			// Initialize if null
			this.selectedGroups = [];
		}

		if (this.tree.length === 0) {
			const unwatch = this.$watch("tree", () => {
				if (this.tree.length > 0) {
					unwatch();
					this.updateFromValue();
				}
			});
			return;
		}

		if (this.value) {
			if (this.multiple) {
				// Value set, and we are using an array of groups

				// Remove any from selected groups list that don't appear in the model
				this.selectedGroups = this.selectedGroups.filter(
					group => !this.value.some(groupID => groupID == group.groupID)
				);

				// Find an add any groups that are in the model but not the groups list
				this.value.forEach(async groupID => {
					if (this.selectedGroups.some(group => group.groupID == groupID)) {
						return;
					}

					const group = await this.findGroupInTreeById(groupID);
					if (group) {
						this.selectedGroups.push(group);
					}
				});
			} else {
				// Already current value - do notoing
				if (this.selectedGroups.some(group => group.groupID == this.value)) {
					return;
				}

				// Set selected groups list to new value
				this.selectedGroups = [];
				const group = await this.findGroupInTreeById(this.value);
				if (group) {
					this.selectedGroups.push(group);
				}
			}
		} else {
			// Value is null, so reset list
			this.selectedGroups = [];
		}
	}

	/**
	 * Selected group
	 */
	public get selectedGroup() {
		if (this.selectedGroups.length == 0) return null;
		return this.selectedGroups[0];
	}

	@Watch("selectedGroups", { deep: true })
	private emitSelectedGroup(selectedGroup: any[]) {
		this.$emit("selected-group", selectedGroup[0]);
	}

	public get selectedGroupIDs() {
		return this.selectedGroups.map((group: any) => group.groupID);
	}

	/**
	 * Select group - sets current if in single, or adds to list in multiple
	 */
	public selectGroup(group): void {
		if (!this.multiple && this.selectedGroups.length > 0) {
			this.selectedGroups = [];
		} else {
			if (this.selectedGroups.some(alreadySelected => alreadySelected.groupID === group.groupID)) {
				return;
			}
		}

		this.selectedGroups.push(group);
		this.areaSearch = "";
		this.setSiteTreeFilter(this.areaSearch);

		if (this.multiple) {
			this.$emit(
				"input",
				this.selectedGroups.map(group => group.groupID)
			);
		} else {
			this.$emit("input", group.groupID);
		}

		if (this.actionOnSelect) {
			this.$store.dispatch(this.actionOnSelect, group.groupID);
		}
	}

	/**
	 * Remove group from multiple group list.
	 */
	public deselectGroup(group): void {
		const groupIndex = this.selectedGroups.findIndex(alreadySelected => alreadySelected.groupID === group.groupID);
		if (groupIndex > -1) this.selectedGroups.splice(groupIndex, 1);

		if (this.multiple) {
			this.$emit(
				"input",
				this.selectedGroups.map(group => group.groupID)
			);
		}
	}

	/**
	 * Update tree view on change
	 */
	@Watch("tree")
	public onTreeChange() {
		if (this.collapsed && this.collapsedLevel) {
			this.collapseSiteTree({
				groups: this.tree,
				lvl: 1,
				collapsedLevel: this.collapsedLevel
			});
		}
		this.$refs.areaTreeScrollBar.update();
	}

	/**
	 * Select a group when clicked on in tree, and close drop down
	 */
	public treeGroupSelected(nodeGroup: any) {
		this.selectGroup(nodeGroup.group);
		this.$refs.searchBox.blur();
	}

	/**
	 * Returns true if the given node has sub-nodes.
	 */
	public nodeHasChildren(nodeGroup: SiteTreeNode): boolean {
		return (
			typeof nodeGroup.subGroups != "undefined" &&
			nodeGroup.subGroups != null &&
			nodeGroup.subGroups.length > 0 &&
			nodeGroup.subGroups.reduce((curr, node) => curr || node.show, false)
		);
	}

	/**
	 * Selects the first visible site node in the tree, if there is one.
	 */
	public selectFirstNode() {
		const getChild = (nodeGroups: SiteTreeNode[]) => {
			for (var child of nodeGroups) {
				if (child.show) {
					if (this.nodeHasChildren(child)) {
						return getChild(child.subGroups);
					} else {
						return child;
					}
				}
			}

			return null;
		};

		var firstNode = getChild(this.tree);
		if (firstNode == null) {
			return;
		} else {
			this.selectGroup(firstNode.groupID);
		}
	}

	/**
	 * Filter area tree
	 */
	@Watch("areaSearch")
	public onAreaSearchChange(value: string, oldValue: string) {
		if (value != oldValue) {
			this.setSiteTreeFilter(value);
		}
	}

	/**
	 * Check if filter has any sites
	 */
	public get anyVisibleSites() {
		return this.tree.reduce((curr, prev) => curr || prev.show, false);
	}

	private onSearchFocus(): void {
		if (this.readonly) {
			return;
		}

		this.filterFocus = true;
	}

	/**
	 * When filter is blurred, close dropdown
	 */
	private onSearchBlur(): void {
		setTimeout(() => {
			this.filterFocus = false;
			this.areaSearch = "";
			this.setSiteTreeFilter(this.areaSearch);

			// Re-expand the site tree
			this.expandSiteTree();
		}, 500);
	}

	private setSearchFocus() {
		if (!this.filterFocus) {
			this.$refs.searchBox.focus();
		}
	}
}
